import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  TextField,
  Grid,
  CircularProgress
} from '@mui/material';
import newImportService from '../../services/newImportService';

/**
 * Admin: configure how often the Sprint Review KPI card (planned / completed / missed /
 * scope creep) auto-refreshes from ADO Analytics for the current sprint.
 *
 * KPIs are computed from ADO Analytics WorkItemSnapshot — a daily historical series that
 * webhooks cannot update — so they are pulled on a cadence rather than event-driven. The
 * scheduler's 30-min checker refreshes only when stored KPI data is older than this value.
 * Persisted on AdoConfig.kpiRefreshIntervalHours (1–24h, default 4).
 */
const SprintDataRefreshPanel = () => {
  const [hours, setHours] = useState(4);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await newImportService.getSprintKpiConfig();
        if (res.success && res.data && res.data.kpiRefreshIntervalHours != null) {
          setHours(res.data.kpiRefreshIntervalHours);
        }
      } catch (err) {
        setError(err.message || 'Failed to load configuration');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const clamped = Math.min(24, Math.max(1, parseInt(hours, 10) || 4));
      const res = await newImportService.updateSprintKpiConfig(clamped);
      if (res.success) {
        setHours(res.data?.kpiRefreshIntervalHours ?? clamped);
        setSuccess('Sprint data refresh interval saved.');
      } else {
        setError(res.message || 'Failed to save');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Sprint Data Refresh Interval</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        How often the Sprint Review KPI card (planned / completed / missed / scope creep)
        auto-refreshes from ADO Analytics for the current sprint. Snapshots are daily, so
        4 hours is recommended. This is independent of Delta Imports and webhook processing.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Refresh Every (Hours)"
              type="number"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 1, max: 24 }}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="contained" size="medium" onClick={handleSave} disabled={saving} fullWidth>
              {saving ? 'Saving...' : 'Save Interval'}
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default SprintDataRefreshPanel;
